import classnames from 'classnames'
import { Component, MouseEvent } from 'react'
import ReactDOM from 'react-dom/client'

import { TIMEFRAME } from '../../main/constants'
import { apiRequest } from '../../main/services/api'
import { Instrument } from '../../main/types'
import FuturesSparkline from '../futures/components/CanvasSparkline'
import RowsStore from '../futures/stores/rowsStore'
import TilesStore from '../futures/stores/tilesStore'
import { FuturesTimeframe } from '../futures/types'
import { forexDomains } from '../futures/utils'
import { generateColorScale } from '../futures/utils'
import { decodeQueryString, encodeQueryString } from '../queryString'
import { redGreenGradient } from '../shared/gradients'
import * as hoverChart from '../shared/hover-chart'
import ForexActionCreators from './actions/forexActionCreators'
import BarChart from './barChart'
import ChartMenu from './components/ChartMenu'
import LayoutNew from './components/redesign/Layout'
import MenuNew from './components/redesign/Menu'
import { ForexTileMap, ForexTileTicker, PageType, TimeFrames } from './types'

const colorScale = generateColorScale(forexDomains, 'd')

interface MenuProps<Key> {
  isDetailOpen?: boolean
  activePage: string
  timeframes: TimeFrames<Key>
  company?: string
}

class Menu<Key extends string> extends Component<MenuProps<Key>> {
  render() {
    var { activePage } = this.props
    var timeframesMenu = this.props.timeframes

    return (
      <div className="menu">
        <ul className="menu-pages">
          <li className={activePage === 'quotes' ? 'is-active' : ''}>
            <a href="/forex.ashx">Quotes</a>
          </li>
          <li className={activePage === 'performance' ? 'is-active' : ''}>
            <a href="/forex_performance.ashx">Performance</a>
          </li>
          <li className={activePage === 'charts' ? 'is-active' : ''}>
            <a href="/forex_charts.ashx">Charts</a>
          </li>
        </ul>

        <ul className="menu-timeframes">
          {timeframesMenu.timeframes.map((timeframe) => (
            <li className={timeframesMenu.active === timeframe.key ? 'is-active' : ''} key={timeframe.key}>
              <a
                href={timeframesMenu.link(timeframe.key as Key)}
                onClick={timeframesMenu.onClick.bind(this, timeframe.key as Key)}
                title={timeframe.title}
              >
                {timeframe.value}
              </a>
            </li>
          ))}
        </ul>
      </div>
    )
  }
}

interface TileProps {
  ticker: string
  hoverOffsetX: string
  hoverOffsetY: string
  width: number
}

function getTileState(props: TileProps) {
  return TilesStore.getTile(props.ticker)
}

class Tile extends Component<TileProps> {
  state = getTileState(this.props)

  componentDidMount() {
    TilesStore.addChangeListener(this._onChange)
  }

  componentWillUnmount() {
    TilesStore.removeChangeListener(this._onChange)
  }

  _onChange = () => {
    this.setState(getTileState(this.props))
  }

  render() {
    var { ticker, label, last, prevClose, change, high, low, sparkline, sparklineDateChanges } = this.state
    var { hoverOffsetX, hoverOffsetY } = this.props
    var instrument = ticker.length === 6 ? Instrument.Forex : Instrument.Futures

    var format = (n: number) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
    var places = last > 6 ? 2 : 4
    var round = (n: number) => (+(Math.round(Number(n + 'e+' + places)) + 'e-' + places)).toFixed(places)

    var link = instrument === 'forex' ? '/forex_charts.ashx?p=d1&t=' + ticker : '/futures_charts.ashx?p=d1&t=' + ticker

    const chartImage = hoverChart.getHoverChartImg(
      '@' + ticker.toLowerCase(),
      false,
      instrument === 'forex'
        ? FinvizSettings.hoverCharts?.forex?.timeframe
        : FinvizSettings.hoverCharts?.futures?.timeframe,
      instrument === 'forex'
        ? FinvizSettings.hoverCharts?.forex?.dateRange
        : FinvizSettings.hoverCharts?.futures?.dateRange
    )

    const boxover = `cssbody=[hoverchart] cssheader=[tabchrthdr] body=[${chartImage.img}] offsetx=[${hoverOffsetX}] offsety=[${hoverOffsetY}] delay=[100]`

    var color = colorScale(change)

    var range = (high - low) * 0.1
    var highlightHigh = last > high - range
    var highlightLow = last < low + range
    var highlightStyle = {
      background: '#fbdb19',
      color: '#3e424f',
    }

    var opacityChange = Math.min(Math.abs(change), 1)
    var textOpacity = 0.5 + (0.2 / 1) * opacityChange
    var highLowStyle = { color: `rgba(255, 255, 255, ${textOpacity})` }

    return (
      <a
        className="tile"
        href={link}
        style={{ width: this.props.width, marginLeft: instrument === 'forex' && ticker !== 'BTCUSD' ? 1 : 9 }}
        data-boxover={boxover}
      >
        <div className="tile_content" style={{ backgroundColor: color }}>
          <span className="tile_label">{label}</span>
          <span className="tile_last-price">{round(last)}</span>
          <span className="tile_change">
            {format(change)}
            <span className="tile_dot"> &#8226; </span>
            {round(last - prevClose).replace('-', '')}
          </span>
          <div className="tile_high-low" style={highLowStyle}>
            <div style={highlightHigh ? highlightStyle : {}}>
              <span className="tile_high-label">H</span> {round(high)}
            </div>
            <div style={highlightLow ? highlightStyle : {}}>
              <span className="tile_low-label">L</span> {round(low)}
            </div>
          </div>
          <FuturesSparkline
            width={this.props.width}
            height={72}
            data={sparkline}
            dateChanges={sparklineDateChanges}
            change={change}
            changeDomain={1}
          />
        </div>
      </a>
    )
  }
}

class Layout extends Component {
  render() {
    var layout = [
      ['EURUSD', 'GBPUSD', 'USDJPY', 'CL'],
      ['USDCAD', 'USDCHF', 'AUDUSD', 'GC'],
      ['NZDUSD', 'EURGBP', 'GBPJPY', 'BTCUSD'],
    ]

    var tileWidth = 240
    var width = layout[0].length * tileWidth + 16

    return (
      <div style={{ margin: '0 auto', width }}>
        {layout.map((row, ri) => (
          <div key={ri}>
            {row.map((ticker) => (
              <Tile ticker={ticker} key={ticker} width={tileWidth} hoverOffsetX="0" hoverOffsetY="25" />
            ))}
            <div className="clearfix"></div>
          </div>
        ))}
      </div>
    )
  }
}

function getForexState() {
  return {
    tiles: TilesStore.getTiles(),
  }
}

class Forex extends Component {
  state = getForexState()

  componentDidMount() {
    TilesStore.addChangeListener(this._onChange)
  }

  componentWillUnmount() {
    TilesStore.removeChangeListener(this._onChange)
  }

  _onChange = () => {
    this.setState(getForexState())
  }

  render() {
    var timeframes = {
      link: () => '#',
      onClick: (timeframe: string, e: MouseEvent) => {
        e.preventDefault()
        ForexActionCreators.changeTimeframe(timeframe)
      },
      active: RowsStore.getTimeframe(),
      timeframes: [
        { key: 'i5', value: '5M' },
        { key: 'h', value: 'H' },
        { key: 'd', value: 'D' },
        { key: 'w', value: 'W' },
        { key: 'm', value: 'M' },
      ],
    }

    // TODO bud spravit special API alebo preratavat
    var tiles = this.state.tiles as ForexTileMap

    var relativePerf = { USD: 0 }
    Object.keys(tiles).forEach((ticker) => {
      var tile = tiles[ticker as ForexTileTicker]
      if (ticker.indexOf('USD') === -1 || ticker.indexOf('BTC') !== -1) {
        return
      }
      var inverse = ticker.substring(0, 3) === 'USD'
      // @ts-ignore
      relativePerf[ticker.replace('USD', '')] =
        (inverse ? tile.prevClose / tile.last - 1 : tile.last / tile.prevClose - 1) * 100 // todo round
    })

    var rows = Object.keys(relativePerf)
      .map((key) => ({
        label: key,
        // @ts-ignore
        value: relativePerf[key],
      }))
      .sort((a, b) => b.value - a.value)

    const isRedesignEnabled = FinvizSettings.hasRedesignEnabled
    const MenuComponent = isRedesignEnabled ? MenuNew : Menu
    const LayoutComponent = isRedesignEnabled ? LayoutNew : Layout
    //
    return (
      <div className="futures forex flex flex-col">
        <MenuComponent activePage={PageType.Quotes} timeframes={timeframes} />
        <LayoutComponent />
        {!isRedesignEnabled && (
          <>
            <div className="clearfix" />
            <br />
          </>
        )}
        <div
          className={classnames('perf-chart-container', {
            'my-7': isRedesignEnabled,
          })}
        >
          <div className="bar-chart-wrapper">
            <BarChart width={740} data={rows} period="d" {...(isRedesignEnabled && { gradient: redGreenGradient })} />
          </div>
        </div>
      </div>
    )
  }
}

interface ForexPerformanceProps {
  rows: Record<ForexTileTicker, number>
}

class ForexPerformance extends Component<ForexPerformanceProps> {
  declare refreshInterval: number

  state = {
    rows: this.props.rows,
  }

  componentDidMount() {
    this.refreshInterval = window.setInterval(async () => {
      const data = await apiRequest(`/api/forex_perf.ashx${window.location.search}`)
      if (data) {
        this.setState({ rows: data })
      }
    }, 30 * 1000)
  }

  componentWillUnmount() {
    clearInterval(this.refreshInterval)
  }

  render() {
    var view = decodeQueryString()['v'] || '1'
    var kv = { d: 1, w: 2, m: 3, mtd: 8, q: 4, hy: 5, y: 6, ytd: 7 }
    var vk: Record<string, keyof typeof kv> = {}
    Object.keys(kv).forEach((key: any) => {
      vk[kv[key as keyof typeof kv].toString()] = key
    })

    var timeframes = {
      link: (key: string) => '/forex_performance.ashx?v=' + kv[key as keyof typeof kv],
      onClick: () => {},
      active: vk[view],
      timeframes: [
        { key: 'd', value: 'D', title: 'Day' },
        { key: 'w', value: 'W', title: 'Week' },
        { key: 'm', value: 'M', title: 'Month' },
        { key: 'mtd', value: 'MTD', title: 'Month to Date' },
        { key: 'q', value: 'Q', title: 'Quarter' },
        { key: 'hy', value: 'HY', title: 'Half Year' },
        { key: 'y', value: 'Y', title: 'Year' },
        { key: 'ytd', value: 'YTD', title: 'Year To Date' },
      ],
    }

    var rows = Object.keys(this.state.rows)
      .map((key) => ({
        label: key,
        value: this.state.rows[key as ForexTileTicker],
      }))
      .filter((row) => row.label !== 'BTC')
      .sort((a, b) => b.value - a.value)

    const isRedesignEnabled = FinvizSettings.hasRedesignEnabled
    const MenuComponent = isRedesignEnabled ? MenuNew : Menu

    return (
      <div className="futures forex flex flex-col">
        <MenuComponent activePage={PageType.Performance} timeframes={timeframes} />
        <div className="perf-chart-container">
          <div className="bar-chart-wrapper">
            <BarChart
              width={740}
              data={rows}
              onClick={() => {}}
              period={vk[view]}
              {...(isRedesignEnabled && { gradient: redGreenGradient })}
            />
          </div>
        </div>
      </div>
    )
  }
}

interface ChartsState {
  timeframe: TIMEFRAME
  dateRange: string
  ticker: string
  type: string
  isGroupOpen: boolean
  activeGroup: string
}

function getChartsState(override: Partial<ChartsState> = {}) {
  var queryString = decodeQueryString()
  var timeframe = queryString['p'] as TIMEFRAME
  if (!Object.values(TIMEFRAME).includes(timeframe)) {
    timeframe = TIMEFRAME.d
  }
  var ticker = (queryString['t'] || 'ALL').toUpperCase()
  var type = queryString['ty']

  return {
    timeframe,
    dateRange: queryString['r'],
    ticker,
    type,
    ...override,
  }
}

export class ForexCharts extends Component {
  state = getChartsState()

  _onTimeframeChange = async (timeframe: TIMEFRAME) => {
    ForexActionCreators.changeTimeframe(timeframe)
    this.setState(getChartsState({ timeframe }))
  }

  render() {
    const { timeframe, ticker, type, dateRange } = this.state
    const isDetailOpen = ticker !== 'ALL'

    var timeframes = {
      link: (key: string) => `/forex_charts.ashx?${encodeQueryString({ t: ticker, p: key, ty: type, r: dateRange })}`,
      onClick: () => {},
      active: timeframe,
      timeframes: [
        { key: FuturesTimeframe.Minute5, value: '5M' },
        { key: FuturesTimeframe.Hourly, value: 'H' },
        { key: FuturesTimeframe.Daily, value: 'D' },
        { key: FuturesTimeframe.Weekly, value: 'W' },
        { key: FuturesTimeframe.Monthly, value: 'M' },
      ],
    }

    const isRedesignEnabled = FinvizSettings.hasRedesignEnabled
    const MenuComponent = isRedesignEnabled ? MenuNew : Menu

    return (
      <div
        className={classnames('forex flex-col', {
          futures: !isRedesignEnabled,
          'pb-5': isDetailOpen && isRedesignEnabled,
        })}
      >
        <div
          className={classnames({
            'border-b border-gray-100 bg-white dark:border-gray-600 dark:bg-gray-800':
              isRedesignEnabled && isDetailOpen,
          })}
        >
          <div
            className={classnames(
              'mx-auto',
              isRedesignEnabled && {
                'max-w-[79rem]': !isDetailOpen,
                'max-w-[81.5rem] px-5': isDetailOpen,
              }
            )}
          >
            <MenuComponent
              activePage={PageType.Charts}
              timeframes={timeframes}
              isDetailOpen={isDetailOpen}
              company={TilesStore.getTile(ticker)?.label}
              onTimeframeChange={this._onTimeframeChange}
              onDateRangeChange={(dateRange) => this.setState(getChartsState({ dateRange }))}
            />
          </div>
        </div>

        {isDetailOpen && (
          <ChartMenu
            isRedesignEnabled={isRedesignEnabled}
            activeTile={ticker}
            activeTimeframe={timeframe}
            activeRange={dateRange}
            type={type}
            showChartControls={!isRedesignEnabled}
          />
        )}
      </div>
    )
  }
}

window['FinvizInitForex'] = function (tiles: ForexTileMap) {
  ForexActionCreators.initTiles(tiles)
  const rootEl = document.getElementById('forex')
  const root = ReactDOM.createRoot(rootEl!)
  root.render(<Forex />)

  setInterval(ForexActionCreators.update, 30 * 1000)
}

window['FinvizInitForexPerformance'] = function (rows: Record<ForexTileTicker, number>) {
  const rootEl = document.getElementById('futures')
  const root = ReactDOM.createRoot(rootEl!)
  root.render(<ForexPerformance rows={rows} />)
}
